import { FC, useState } from 'react';
import { isArray } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { Bucket, bucketMappingDomain } from '@/common/enums/qiniu';
import { IMAGE_TYPES, VIDEO_TYPES } from 'src/common/utils/file-types';
import styles from './Upload.module.less';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormUpload from '@/common/components/Form/V2FormUpload/V2FormUpload';
import UploadFileExamples from './UploadFileExamples';


/**
 * @Description 上传组件
 */
const Upload: FC<{
  propertyItem: any;
  disabled?: boolean;
  required?: boolean;
}> = ({
  propertyItem,
  disabled,
  required,
}) => {
  const label = propertyItem.anotherName || propertyItem.name || '';

  const restriction = propertyItem.restriction ? JSON.parse(propertyItem.restriction) : {};

  const placeholder = restriction.placeholder ? restriction.placeholder : `请上传${label || ''}`;
  const extraTips = restriction.extraTips || ''; // 提示语
  const rules = [
    { required, message: placeholder },
  ];

  const _accept = isArray(restriction?.accept)
    ? restriction.accept.map((item: any) => String(item).replace(/^\./, '').toLowerCase())
    : [];
  // 全命中才判该类型，避免混合后缀（如 ['.jpg', '.mp4']）走到歧义分支
  const acceptImage = _accept.length > 0 && _accept.every((val: string) => IMAGE_TYPES.includes(val));
  const acceptVideo = _accept.length > 0 && _accept.every((val: string) => VIDEO_TYPES.includes(val));

  // 决定上传类型，图片/视频/文件
  // 优先级：accept 为空 → file 兜底；全命中图片 → image；全命中视频 → video；其余（含混合、文件类、未知后缀）→ file
  const uploadType: 'image' | 'video' | 'file' = !_accept.length
    ? 'file'
    : (acceptImage ? 'image' : (acceptVideo ? 'video' : 'file'));
  // 产品甜瓜让拿掉仅一个的限制，如果性能和视频流量又问题，用户自己承担
  // const uploadType = acceptFile ? 'file' : acceptVideo ? (restriction.maxCount > 1 ? 'file' : 'video') : 'image';

  // 内部组件能通过格式检查的后缀，注意需要将接口返回的类型去掉前缀点'.' ex ['.mov', '.mp4'] => ['mov','mp4']
  const fileType = Array.isArray(_accept) && _accept.length ? _accept.map(itm => (itm && itm.substring(0, 1) === '.') ? itm.substring(1, itm.length) : itm) : undefined;

  /** 文件示例 */
  /** 当前上传类型下的示例配置对象（单一对象，不是数组） */
  const fileExampleConfig = restriction.fileExampleConfig || null;
  /** 是否有可展示的示例数据：配置对象存在 且 examples 数组非空 */
  const hasExamples = !!(fileExampleConfig &&
    Array.isArray(fileExampleConfig.examples) &&
    fileExampleConfig.examples.length);

  /** 按钮文案（image/video → 视频示例；file → 文件示例） */
  const exampleBtnTextMap: Record<'image' | 'video' | 'file', string> = {
    image: '图片示例',
    video: '视频示例',
    file: '文件示例',
  };
  /** 弹窗标题文案 */
  const exampleModalTitleMap: Record<'image' | 'video' | 'file', string> = {
    image: `${label} · 图片示例`,
    video: `${label} · 视频示例`,
    file: `${label} · 文件示例`,
  };

  /** 示例弹窗开关 */
  const [exampleOpen, setExampleOpen] = useState(false);

  /** 带「示例」按钮的 label 节点（V2FormUpload.label 接受 ReactNode） */
  const labelNode = hasExamples
    ? (
      <span className={styles.uploadLabel}>
        {label}
        <a className={styles.exampleBtn} onClick={() => methods.openExample()}>
          {exampleBtnTextMap[uploadType]}
        </a>
      </span>
    )
    : label;

  let qiniuParams: any = {};
  if (uploadType === 'file') {
    qiniuParams = {
      domain: bucketMappingDomain['linhuiba-file'],
      bucket: Bucket.File,
    };
  } else if (uploadType === 'video') {
    qiniuParams = {
      domain: bucketMappingDomain['linhuiba-video'],
      bucket: Bucket.Video,
    };
  } else if (uploadType === 'image') {
    qiniuParams = {
      domain: bucketMappingDomain['linhuiba-certs'],
      bucket: Bucket.Certs,
    };
  }

  const textValue = propertyItem.textValue ? JSON.parse(propertyItem.textValue) : [];

  const methods = useMethods({
    /**
     * 打开示例弹窗
     */
    openExample() {
      setExampleOpen(true);
    },
    /**
     * 关闭示例弹窗
     */
    closeExample() {
      setExampleOpen(false);
    },
  });

  // 禁用且无数据时优化展示'-' ，其他情况下无影响
  return (
    <>
      {disabled && (!isArray(textValue) || !textValue.length)
        ? <V2FormInput
          label={label}
          placeholder='-'
          disabled
        /> : <V2FormUpload
          label={labelNode}
          name={propertyItem.identification}
          uploadType={uploadType}
          breakTheNorm={uploadType === 'video'}
          verticalMiddleHelp={extraTips
            ? <div className='fs-12 c-999 mb-8'>
              {extraTips}
            </div> : <></>}
          config={{
            qiniuParams,
            size: restriction.size,
            maxCount: restriction.maxCount,
            fileType,
            accept: Array.isArray(restriction.accept) ? restriction.accept : undefined, // 这里用处理之前的 restriction.accept
            disabled,
            onRemove: disabled ? () => false : undefined
          }}
          rules={rules}
          required={required}
          imageTypePreviewGroup
        />
      }

      {/* 示例弹窗：仅在存在示例数据时挂载 */}
      {hasExamples && (
        <UploadFileExamples
          open={exampleOpen}
          onClose={methods.closeExample}
          uploadType={uploadType}
          title={exampleModalTitleMap[uploadType]}
          config={fileExampleConfig}
        />
      )}

    </>
  );
};

export default Upload;
